import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  PoundSterling, 
  TrendingUp, 
  Clock, 
  Plus,
  ArrowRight,
  Activity,
  FileText,
  UserCheck,
  AlertCircle,
  PartyPopper,
  FileSpreadsheet,
  Calculator,
  Wallet,
  Receipt,
  TrendingDown,
  Building2,
  Shield,
  Key,
  UserCog,
  Lock,
  Mic
} from 'lucide-react';
import { FlagsSummaryWidget, ResponseTimeAlertsWidget, MissedAppointmentsWidget, DashboardAlertsBanner } from '../components/ClinicalFlags';
import ClockInOut from '../components/ClockInOut';
import { VoiceCommandWidget } from '../components/VoiceCommand';
import { toast } from 'sonner';

export default function Dashboard() {
  const { api, user, isDirector, canAccessFinance } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [accountSheets, setAccountSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessStats, setAccessStats] = useState({ grants: 0, blocks: 0 });
  
  // Super Admin check
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const promises = [
        api.get('/dashboard/stats'),
        api.get('/appointments', { params: { limit: 5 } })
      ];
      
      // If director, also fetch pending approvals count and notifications
      if (user?.role === 'director') {
        promises.push(api.get('/auth/pending-count'));
        promises.push(api.get('/auth/notifications'));
      }
      
      // Fetch financial data for finance users
      if (canAccessFinance) {
        promises.push(api.get('/accounts-excel/sheets').catch(() => ({ data: [] })));
      }
      
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setRecentAppointments(results[1].data.slice(0, 5));
      
      if (user?.role === 'director' && results.length > 2) {
        setPendingCount(results[2].data.count || 0);
        setNotifications(results[3].data || []);
      }
      
      // Set financial data
      if (canAccessFinance) {
        const sheetsData = results[results.length - 1]?.data || [];
        setAccountSheets(sheetsData);
        
        // Calculate summary from Balance sheet if available
        const balanceSheet = sheetsData.find(s => s.name === 'Balance');
        if (balanceSheet) {
          setFinancialSummary({
            totalSheets: sheetsData.length,
            totalFormulas: sheetsData.reduce((sum, s) => sum + (s.formulas_count || 0), 0),
            hasData: sheetsData.length > 0
          });
        }
      }
      
      // Fetch access grants/blocks stats for Super Admin
      if (user?.email?.toLowerCase() === 'admin@icrcl.co.uk') {
        try {
          const [grantsRes, blocksRes] = await Promise.all([
            api.get('/auth/access-grants'),
            api.get('/auth/access-blocks')
          ]);
          setAccessStats({
            grants: grantsRes.data?.length || 0,
            blocks: blocksRes.data?.length || 0
          });
        } catch (e) {
          console.log('Access stats not available');
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, gradient, trend, subtitle, statKey, editable }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    
    const handleSave = () => {
      setStats(prev => ({ ...prev, [statKey]: typeof value === 'string' && value.includes('£') ? parseFloat(editValue) : parseInt(editValue) }));
      setIsEditing(false);
      toast.success(`${title} updated`);
    };
    
    return (
      <Card className={`${gradient} text-white overflow-hidden relative`}>
        <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
          <Icon className="w-full h-full" />
        </div>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-white/80 text-sm font-medium">{title}</p>
              {isEditing && isSuperAdmin && editable ? (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-24 px-2 py-1 text-xl font-bold text-slate-800 rounded"
                    autoFocus
                  />
                  <button onClick={handleSave} className="text-white/80 hover:text-white text-sm">✓</button>
                  <button onClick={() => setIsEditing(false)} className="text-white/80 hover:text-white text-sm">✕</button>
                </div>
              ) : (
                <p 
                  className={`text-3xl font-bold mt-2 ${isSuperAdmin && editable ? 'cursor-pointer hover:opacity-80' : ''}`}
                  onClick={() => isSuperAdmin && editable && setIsEditing(true)}
                >
                  {value}
                </p>
              )}
              {subtitle && <p className="text-white/60 text-xs mt-1">{subtitle}</p>}
            </div>
            {trend && (
              <Badge className="bg-white/20 text-white border-0">
                <TrendingUp className="w-3 h-3 mr-1" />
                {trend}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Pending Approvals Alert for Directors */}
      {isDirector && pendingCount > 0 && (
        <Card className="card-icrcl border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-800">
                    {pendingCount} Pending User Approval{pendingCount > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-amber-600">
                    New staff members are waiting for your approval to access the system
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/users')}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                data-testid="review-approvals-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening at ICRCL today
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Voice Command for Super Admin */}
          {isSuperAdmin && <VoiceCommandWidget />}
          
          {/* Hide clinical buttons for admin, hr, finance, support staff */}
          {!['admin', 'hr', 'finance', 'support'].includes(user?.role) && (
          <>
          <Button
            onClick={() => navigate('/patients')}
            variant="outline"
            className="rounded-full"
          >
            <Users className="w-4 h-4 mr-2" />
            View Patients
          </Button>
          <Button
            onClick={() => navigate('/assessments/new')}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
            data-testid="new-assessment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Assessment
          </Button>
          </>
          )}
          {/* Finance-specific header buttons */}
          {user?.role === 'finance' && (
          <>
          <Button
            onClick={() => navigate('/finance')}
            variant="outline"
            className="rounded-full"
          >
            <Receipt className="w-4 h-4 mr-2" />
            View Invoices
          </Button>
          <Button
            onClick={() => navigate('/payroll')}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Payroll
          </Button>
          </>
          )}
        </div>
      </div>

      {/* Clock In/Out Card */}
      <ClockInOut />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={stats?.total_patients || 0}
          icon={Users}
          gradient="stat-card-navy"
          subtitle="Active children"
          statKey="total_patients"
          editable={isSuperAdmin}
        />
        <StatCard
          title="Today's Appointments"
          value={stats?.today_appointments || 0}
          icon={Calendar}
          gradient="stat-card-teal"
          subtitle={new Date().toLocaleDateString('en-GB', { weekday: 'long' })}
          statKey="today_appointments"
          editable={isSuperAdmin}
        />
        {user?.role === 'therapist' ? (
          <StatCard
            title="My Patients"
            value={stats?.my_patients_count || 0}
            icon={ClipboardList}
            gradient="stat-card-green"
            subtitle="Assigned to you"
            statKey="my_patients_count"
            editable={isSuperAdmin}
          />
        ) : (
          <StatCard
            title="Pending Assessments"
            value={stats?.pending_assessments || 0}
            icon={ClipboardList}
            gradient="stat-card-green"
            subtitle="Awaiting completion"
            statKey="pending_assessments"
            editable={isSuperAdmin}
          />
        )}
        {canAccessFinance ? (
          <StatCard
            title="Total Revenue"
            value={`£${(stats?.total_revenue || 0).toLocaleString()}`}
            icon={PoundSterling}
            gradient="stat-card-pink"
            statKey="total_revenue"
            editable={isSuperAdmin}
          />
        ) : (
          <StatCard
            title="My Appointments Today"
            value={stats?.my_appointments_today || 0}
            icon={Clock}
            gradient="stat-card-pink"
            statKey="my_appointments_today"
            editable={isSuperAdmin}
          />
        )}
      </div>

      {/* Dashboard-wide Alerts Banner - UK Healthcare Standards */}
      {['director', 'clinical_lead', 'educational_lead', 'therapist', 'admin', 'reception'].includes(user?.role) && (
        <DashboardAlertsBanner />
      )}

      {/* Clinical Alerts Grid - Response Time + Missed Appointments + Documentation Flags */}
      {['director', 'clinical_lead', 'educational_lead', 'therapist'].includes(user?.role) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ResponseTimeAlertsWidget />
          <MissedAppointmentsWidget />
          <FlagsSummaryWidget />
        </div>
      )}

      {/* Financial Overview - for directors only (Accounts Excel restricted) */}
      {user?.role === 'director' && accountSheets.length > 0 && (
        <Card className="card-icrcl border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-emerald-800">ICRCL Accounts</CardTitle>
                  <CardDescription className="text-emerald-600">
                    Financial tracking with {financialSummary?.totalFormulas || 0} live formulas
                  </CardDescription>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/finance/accounts')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Open Accounts
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-2">
              {accountSheets.map((sheet) => (
                <div 
                  key={sheet.id}
                  className="p-3 bg-white rounded-lg border border-emerald-100 hover:border-emerald-300 cursor-pointer transition-colors"
                  onClick={() => navigate('/finance/accounts')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Calculator className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-medium text-emerald-700 truncate">{sheet.name}</span>
                  </div>
                  {sheet.formulas_count > 0 && (
                    <p className="text-xs text-slate-500">
                      {sheet.formulas_count} formulas
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule */}
        <Card className="lg:col-span-2 card-icrcl">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg text-[#1e3a5f]">Today's Schedule</CardTitle>
              <CardDescription>Upcoming appointments</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentAppointments.length > 0 ? (
              <div className="space-y-4">
                {recentAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#2a9d8f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {apt.patient_name || 'Unknown Patient'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {apt.appointment_type} • {apt.therapist_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#1e3a5f]">{apt.start_time}</p>
                      <Badge
                        variant="outline"
                        className={
                          apt.status === 'completed' ? 'badge-completed' :
                          apt.status === 'scheduled' ? 'badge-pending' :
                          apt.status === 'cancelled' ? 'badge-cancelled' :
                          'badge-active'
                        }
                      >
                        {apt.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No appointments scheduled for today</p>
                <Button
                  variant="link"
                  className="text-[#2a9d8f]"
                  onClick={() => navigate('/calendar')}
                >
                  Schedule an appointment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions - Different for admin vs clinical staff */}
        <Card className="card-icrcl">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Quick Actions</CardTitle>
            <CardDescription>Common tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Clinical actions - hidden from admin, hr, finance, support staff */}
            {!['admin', 'hr', 'finance', 'support'].includes(user?.role) && (
            <>
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/patients')}
              data-testid="quick-add-patient"
            >
              <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mr-3">
                <Users className="w-5 h-5 text-[#1e3a5f]" />
              </div>
              <div className="text-left">
                <p className="font-medium">Add New Patient</p>
                <p className="text-xs text-slate-500">Register a new child</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/assessments/new')}
              data-testid="quick-new-assessment"
            >
              <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center mr-3">
                <ClipboardList className="w-5 h-5 text-[#2a9d8f]" />
              </div>
              <div className="text-left">
                <p className="font-medium">New Assessment</p>
                <p className="text-xs text-slate-500">OT, PT, or SLT assessment</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/evaluations')}
              data-testid="quick-new-report"
            >
              <div className="w-10 h-10 rounded-full bg-[#4ade80]/10 flex items-center justify-center mr-3">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium">Evaluation Report</p>
                <p className="text-xs text-slate-500">Generate new report</p>
              </div>
            </Button>
            </>
            )}
            
            {/* Admin actions - shown to admin staff */}
            {user?.role === 'admin' && (
            <>
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/staff')}
            >
              <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mr-3">
                <Users className="w-5 h-5 text-[#1e3a5f]" />
              </div>
              <div className="text-left">
                <p className="font-medium">Manage Staff</p>
                <p className="text-xs text-slate-500">View staff directory</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/hr')}
            >
              <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center mr-3">
                <Building2 className="w-5 h-5 text-[#2a9d8f]" />
              </div>
              <div className="text-left">
                <p className="font-medium">HR & Workforce</p>
                <p className="text-xs text-slate-500">Leave, training, DBS</p>
              </div>
            </Button>
            </>
            )}
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/calendar')}
            >
              <div className="w-10 h-10 rounded-full bg-[#f472b6]/10 flex items-center justify-center mr-3">
                <Calendar className="w-5 h-5 text-pink-500" />
              </div>
              <div className="text-left">
                <p className="font-medium">Schedule Session</p>
                <p className="text-xs text-slate-500">Book an appointment</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/events')}
              data-testid="quick-events"
            >
              <div className="w-10 h-10 rounded-full bg-[#a855f7]/10 flex items-center justify-center mr-3">
                <PartyPopper className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-left">
                <p className="font-medium">Events & Activities</p>
                <p className="text-xs text-slate-500">Manage events and photos</p>
              </div>
            </Button>
            
            {/* Finance actions - shown to finance staff */}
            {user?.role === 'finance' && (
            <>
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/finance')}
            >
              <div className="w-10 h-10 rounded-full bg-[#f59e0b]/10 flex items-center justify-center mr-3">
                <PoundSterling className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-medium">Finance Manager</p>
                <p className="text-xs text-slate-500">Manage invoices and payments</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/payroll')}
            >
              <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center mr-3">
                <Wallet className="w-5 h-5 text-[#2a9d8f]" />
              </div>
              <div className="text-left">
                <p className="font-medium">Payroll Manager</p>
                <p className="text-xs text-slate-500">Staff salaries and payslips</p>
              </div>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start rounded-lg h-auto py-3"
              onClick={() => navigate('/rti')}
            >
              <div className="w-10 h-10 rounded-full bg-[#6366f1]/10 flex items-center justify-center mr-3">
                <Receipt className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <p className="font-medium">HMRC & RTI</p>
                <p className="text-xs text-slate-500">Tax submissions</p>
              </div>
            </Button>
            </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity & Insights for Directors */}
      {isDirector && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">Staff Overview</CardTitle>
              <CardDescription>Team performance summary</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Total Therapists</span>
                  <span className="font-bold text-[#1e3a5f]">{stats?.total_therapists || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Pending Invoices</span>
                  <span className="font-bold text-amber-600">{stats?.pending_invoices || 0}</span>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/staff')}
                >
                  View Staff Directory
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">System Health</CardTitle>
              <CardDescription>Platform status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-slate-600">All systems operational</span>
                </div>
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-[#2a9d8f]" />
                  <span className="text-slate-600">AI Assistant: Active</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#1e3a5f]" />
                  <span className="text-slate-600">Audit logging: Enabled</span>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/audit')}
                >
                  View Audit Logs
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Super Admin Permission Delegation Card */}
      {isSuperAdmin && (
        <Card className="card-icrcl border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50" data-testid="permission-delegation-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-indigo-800">Permission Delegation</CardTitle>
                  <CardDescription className="text-indigo-600">
                    Grant specific rights to trusted staff without full admin access
                  </CardDescription>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/access-management')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="manage-permissions-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Manage Permissions
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Active Grants */}
              <div className="p-4 bg-white rounded-lg border border-indigo-100 hover:border-indigo-300 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-slate-700">Active Grants</span>
                </div>
                <p className="text-2xl font-bold text-indigo-700">{accessStats.grants}</p>
                <p className="text-xs text-slate-500 mt-1">Special permissions granted</p>
              </div>
              
              {/* Active Blocks */}
              <div className="p-4 bg-white rounded-lg border border-indigo-100 hover:border-indigo-300 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <Lock className="w-5 h-5 text-red-500" />
                  <span className="font-medium text-slate-700">Access Blocks</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{accessStats.blocks}</p>
                <p className="text-xs text-slate-500 mt-1">Restricted users</p>
              </div>
              
              {/* Quick Actions */}
              <div className="p-4 bg-white rounded-lg border border-indigo-100 hover:border-indigo-300 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <UserCog className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-slate-700">Quick Actions</span>
                </div>
                <div className="space-y-2 mt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full text-xs justify-start"
                    onClick={() => navigate('/access-management?tab=grants')}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Grant Permission
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full text-xs justify-start"
                    onClick={() => navigate('/access-management?tab=users')}
                  >
                    <Users className="w-3 h-3 mr-1" />
                    View All Users
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Delegation Tips */}
            <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-sm text-indigo-700">
                <strong>Tip:</strong> Delegate specific permissions like <span className="font-mono bg-white px-1 rounded">form_config:manage</span> to Lead Therapists, 
                or <span className="font-mono bg-white px-1 rounded">hr:manage_policies</span> to HR staff without giving them full admin access.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
